import { useState } from "react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import type { WeeklyAvailability } from "@applicant-reviewer/api/availability";
import { DAYS_OF_WEEK, SLOTS_PER_DAY, SLOT_STATUS } from "@applicant-reviewer/api/availability";
import ZoomableScheduleCanvas from "../../../../components/availability/ZoomableScheduleCanvas";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface AvailabilitySectionProps {
  profile: CandidateProfile;
}

function createBlankSchedule(): WeeklyAvailability {
  return DAYS_OF_WEEK.map((day) => ({
    dayOfWeek: day,
    slots: Array.from({ length: SLOTS_PER_DAY }, (_, index) => ({
      index,
      status: SLOT_STATUS.UNAVAILABLE as 0,
    })),
  })) as WeeklyAvailability;
}

export default function AvailabilitySection({ profile }: AvailabilitySectionProps) {
  const [availability, setAvailability] = useState<WeeklyAvailability>(
    profile.availability ?? createBlankSchedule(),
  );
  const [saved, setSaved] = useState(false);
  const updateProfile = useUpdateProfile();

  const handleSave = () => {
    setSaved(false);
    updateProfile.mutate({ availability }, { onSuccess: () => setSaved(true) });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-2">
        <h2 className="text-base font-bold text-gray-900">Weekly Availability</h2>
        <p className="text-xs text-gray-500 mt-1">
          Tap cells to mark when you are available or prefer to work.
        </p>
      </div>

      <ZoomableScheduleCanvas
        availability={availability}
        onChange={setAvailability}
        isManagerMode={false}
      />

      <div className="px-5 pb-5 pt-3 space-y-2">
        {updateProfile.isError && (
          <p className="text-xs text-red-600">Save failed. Please try again.</p>
        )}
        {saved && !updateProfile.isPending && (
          <p className="text-xs text-green-600 font-semibold">Saved!</p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={updateProfile.isPending}
          className="w-full h-12 rounded-full bg-[#0156FF] text-white text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {updateProfile.isPending ? "Saving..." : "Save Availability"}
        </button>
      </div>
    </div>
  );
}
